import React, { useState } from 'react';
import ScoreBar from './ScoreBar';
import JsonCodeBlock from './JsonCodeBlock';

interface CitationCardProps {
  citation: {
    id: number;
    source_type: string;
    source_id: number | null;
    discovery_method: string;
    relevance_score: number | null;
    similarity_score: number | null;
    ranking_position: number | null;
    pipeline_stage?: string;
    selection_predicate?: Record<string, any>;
    llm_reasoning?: string | null;
  };
}

const CitationCard: React.FC<CitationCardProps> = ({ citation }) => {
  const [showReasoning, setShowReasoning] = useState(false);
  const [showPredicate, setShowPredicate] = useState(false);

  const getStatusBadgeColor = (method: string) => {
    const normalized = method.toLowerCase();
    if (normalized.includes('semantic') || normalized.includes('embedding')) {
      return 'bg-blue-50 text-blue-700 ring-blue-600/20';
    }
    if (normalized.includes('keyword') || normalized.includes('exact')) {
      return 'bg-green-50 text-green-700 ring-green-600/20';
    }
    return 'bg-gray-50 text-gray-700 ring-gray-500/10';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {citation.ranking_position !== null && (
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-sm font-semibold">
              #{citation.ranking_position}
            </span>
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">
                {citation.source_type}
              </span>
              {citation.source_id !== null && (
                <span className="text-sm text-gray-500">ID: {citation.source_id}</span>
              )}
            </div>
            {citation.pipeline_stage && (
              <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-700/10 mt-1">
                {citation.pipeline_stage}
              </span>
            )}
          </div>
        </div>
        <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${getStatusBadgeColor(citation.discovery_method)}`}>
          {citation.discovery_method}
        </span>
      </div>

      <div className="space-y-3">
        {citation.similarity_score !== null && (
          <ScoreBar
            score={citation.similarity_score}
            maxScore={1}
            label="Similarity"
            showPercentage={true}
          />
        )}

        {citation.relevance_score !== null && (
          <ScoreBar
            score={citation.relevance_score}
            maxScore={5}
            label="Relevance"
            showPercentage={false}
          />
        )}

        {citation.llm_reasoning && (
          <div className="space-y-2">
            <button
              onClick={() => setShowReasoning(!showReasoning)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              <span>LLM Reasoning</span>
              <svg
                className={`h-4 w-4 transition-transform duration-200 ${
                  showReasoning ? 'transform rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showReasoning && (
              <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{citation.llm_reasoning}</p>
              </div>
            )}
          </div>
        )}

        {citation.selection_predicate && Object.keys(citation.selection_predicate).length > 0 && (
          <div className="mt-4">
            <JsonCodeBlock
              data={citation.selection_predicate}
              title="Selection Predicate"
              defaultExpanded={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CitationCard;
